import {
  EVENT_CONFIRMATION_DISMISS_INTERVAL_MINUTES,
  SCHEDULE_STORAGE_KEY,
} from '@/core/constants/schedule/notification';
import {
  enrichEventsWithCustomerNames,
  matchEvent,
} from '@/utilities/schedule/event';
import { SchedulePolicy } from '@/core/policies/schedule/SchedulePolicy';
import { getUserDescendants } from '@/repositories/Hierarchy';
import { getUserEvents } from '@/repositories/Schedule';
import DateFormats from '@/core/constants/DateFormats';
import moment from 'moment';

export default {
  namespaced: true,
  state: {
    now: moment().toISOString(),
    todayEvents: [],
    descendants: [],
    descendantsPromise: null,
    descendantsLoadedFor: null,
    descendantsLoadingFor: null,
    dismissedEvents: {},
    isLoadingTodayEvents: false,
    isLoadingDescendants: false,
  },
  getters: {
    todayEvents: (state) => state.todayEvents,
    dismissedEvents: (state) => state.dismissedEvents,
    isLoadingTodayEvents: (state) => state.isLoadingTodayEvents,
    isLoadingDescendants: (state) => state.isLoadingDescendants,
    pendingConfirmationEvents: (state) => {
      return state.todayEvents.filter((event) => {
        if (event.execution) return false;

        const startDate = moment(event.startDate);
        const eventConfirmationLimit = event.confirmationDeadline;

        return moment(state.now).isBetween(startDate, eventConfirmationLimit);
      });
    },
    shouldShowConfirmationModal: (state, getters) => {
      return getters.pendingConfirmationEvents.some((event) => {
        const dismissedAt = state.dismissedEvents[event.id];
        if (!dismissedAt) return true;

        return (
          moment(state.now).diff(moment(dismissedAt), 'minutes') >=
          EVENT_CONFIRMATION_DISMISS_INTERVAL_MINUTES
        );
      });
    },
    customersFromDescendants: (state) => {
      const seen = new Set();

      return state.descendants
        .map((hierarchy) => hierarchy.customer)
        .filter((customer) => {
          if (!customer || seen.has(customer.code)) return false;

          seen.add(customer.code);
          return true;
        });
    },
    hierarchyFromDescendants: (state) => (visibleRoles) => {
      return visibleRoles.map((role) => {
        const seen = new Set();
        const users = state.descendants
          .map((item) => item[role])
          .filter((user) => {
            if (!user || seen.has(user.code)) return false;
            seen.add(user.code);
            return true;
          });
        return { role, users };
      });
    },
  },
  mutations: {
    SET_NOW(state) {
      state.now = moment().toISOString();
    },
    SET_TODAY_EVENTS(state, events) {
      state.todayEvents = events;
    },
    UPDATE_EVENT(state, updated) {
      const index = state.todayEvents.findIndex((e) => matchEvent(e, updated));
      if (index === -1) return;

      state.todayEvents.splice(index, 1, {
        ...updated,
        customer: state.todayEvents[index].customer,
      });
    },
    ADD_OR_UPDATE_EVENT_INVITATION(state, { eventId, invitation }) {
      state.todayEvents.forEach((event, index) => {
        if (event.id !== eventId) return;

        const exists = event.invitations.some(
          (inv) => inv.id === invitation.id
        );
        const invitations = exists
          ? event.invitations.map((inv) =>
              inv.id === invitation.id ? invitation : inv
            )
          : [...event.invitations, invitation];

        state.todayEvents.splice(index, 1, { ...event, invitations });
      });
    },
    REMOVE_EVENT_INVITATION(state, { eventId, invitationId }) {
      state.todayEvents.forEach((event, index) => {
        if (event.id !== eventId) return;

        const invitations = event.invitations.filter(
          (inv) => inv.id !== invitationId
        );
        state.todayEvents.splice(index, 1, { ...event, invitations });
      });
    },
    ADD_OR_UPDATE_EVENT_COMMENT(state, { eventId, comment }) {
      state.todayEvents.forEach((event, index) => {
        const seriesId = event.parentId ?? event.id;
        if (seriesId !== eventId) return;

        const exists = event.comments?.some((c) => c.id === comment.id);
        const comments = exists
          ? event.comments.map((c) => (c.id === comment.id ? comment : c))
          : [...(event.comments ?? []), comment];

        state.todayEvents.splice(index, 1, { ...event, comments });
      });
    },
    REMOVE_EVENT_COMMENT(state, { eventId, commentId }) {
      state.todayEvents.forEach((event, index) => {
        const seriesId = event.parentId ?? event.id;
        if (seriesId !== eventId) return;

        const comments = event.comments.filter((c) => c.id !== commentId);
        state.todayEvents.splice(index, 1, { ...event, comments });
      });
    },
    SET_DESCENDANTS(state, descendants) {
      state.descendants = descendants;
    },
    SET_DESCENDANTS_PROMISE(state, promise) {
      state.descendantsPromise = promise;
    },
    SET_DESCENDANTS_LOADED_FOR(state, { userId, channel }) {
      state.descendantsLoadedFor = { userId, channel };
    },
    SET_DESCENDANTS_LOADING_FOR(state, { userId, channel }) {
      state.descendantsLoadingFor = { userId, channel };
    },
    SET_IS_LOADING_TODAY_EVENTS(state, isLoading) {
      state.isLoadingTodayEvents = isLoading;
    },
    SET_IS_LOADING_DESCENDANTS(state, isLoading) {
      state.isLoadingDescendants = isLoading;
    },
    SET_LAST_DISMISSED_AT(state, eventId) {
      state.dismissedEvents = {
        ...state.dismissedEvents,
        [eventId]: state.now,
      };
    },
  },
  actions: {
    async loadDescendants({ commit, state }, { userId, channel }) {
      if (!SchedulePolicy.canList() || !userId || !channel) return;

      const alreadyLoaded =
        state.descendantsLoadedFor?.userId == userId &&
        state.descendantsLoadedFor?.channel === channel;

      if (alreadyLoaded) return { data: state.descendants, error: null };
      if (state.descendantsPromise) return state.descendantsPromise;

      const promise = (async () => {
        commit('SET_IS_LOADING_DESCENDANTS', true);
        commit('SET_DESCENDANTS_LOADING_FOR', { userId, channel });

        try {
          const descendants = await getUserDescendants(userId, channel);
          commit('SET_DESCENDANTS', descendants);
          commit('SET_DESCENDANTS_LOADED_FOR', { userId, channel });
          return { data: descendants, error: null };
        } catch (err) {
          commit('SET_DESCENDANTS', []);
          if (err?.code === 'ERR_CANCELED') return { data: null, error: null };
          return {
            data: null,
            error: 'Error al cargar la jerarquía del usuario',
          };
        } finally {
          commit('SET_DESCENDANTS_PROMISE', null);
          commit('SET_IS_LOADING_DESCENDANTS', false);
        }
      })();

      commit('SET_DESCENDANTS_PROMISE', promise);
      return promise;
    },
    async loadTodayEvents({ commit, dispatch, getters, rootGetters }) {
      const user = rootGetters['auth/user'];
      if (!SchedulePolicy.canList() || !user) return;

      commit('SET_IS_LOADING_TODAY_EVENTS', true);

      const userId = user.id;
      const channel = user.VTWEG;

      try {
        const today = moment().format(DateFormats.ISO_DATE);

        const [data] = await Promise.all([
          getUserEvents(userId, { from: today, to: today }),
          dispatch('loadDescendants', { userId, channel }),
        ]);

        // FILTER-OUT INVITATION EVENTS FROM REMINDER MODAL
        const ownedEvents = data.filter(
          (event) => event.creator?.user?.id === userId
        );

        // FILL EVENTS WITH CUSTOMER NAMES FROM DESCENDANTS OR FETCH FOR INFO (OTHER CHANNELS)
        const events = await enrichEventsWithCustomerNames(
          ownedEvents,
          getters.customersFromDescendants
        );

        commit('SET_TODAY_EVENTS', events);
        return { events, error: null };
      } catch (err) {
        if (err?.code === 'ERR_CANCELED') return { data: null, error: null };

        return {
          data: null,
          error: 'Error al cargar los eventos de hoy',
        };
      } finally {
        commit('SET_IS_LOADING_TODAY_EVENTS', false);
      }
    },
    dismissConfirmation({ commit }, eventId) {
      commit('SET_LAST_DISMISSED_AT', eventId);
    },
    notifyUpdate() {
      localStorage.setItem(SCHEDULE_STORAGE_KEY, Date.now());
    },
  },
};
